/**
 * Format Utilities
 * 
 * Provides utility functions for formatting and masking sensitive data including
 * email masking, phone number formatting, and number cleaning for backend API
 * communication. Handles country code normalization and leading zero removal.
 */

/**
 * Masks an email address for privacy (e.g., "john@example.com" → "j***@example.com")
 * 
 * @param {string} email - The email address to mask
 * @returns {string} Masked email address or empty string if invalid
 * 
 * @example
 * maskEmail('john.doe@example.com') // Returns 'j***@example.com'
 * maskEmail('a@example.com') // Returns 'a@example.com'
 */
export const maskEmail = (email) => {
  if (!email || typeof email !== 'string') { return ''; }

  const [localPart, domain] = email.split('@');

  if (!localPart || !domain) { return email; }

  const maskedLocal = localPart.length > 1
    ? `${localPart[0]}${'*'.repeat(Math.min(3, localPart.length - 1))}`
    : localPart;

  return `${maskedLocal}@${domain}`;
};

/**
 * Formats a phone number with country code prefix
 * 
 * Normalizes phone numbers to include country code, handles leading zeros,
 * and ensures consistent formatting. Defaults to Ghana country code (+233).
 * 
 * @param {string} phoneNumber - The phone number to format
 * @param {string} countryCode - Country code with or without + prefix (default: '+233')
 * @returns {string} Formatted phone number with country code
 * 
 * @example
 * formatPhoneNumber('0241234567') // Returns '+233241234567'
 * formatPhoneNumber('233241234567') // Returns '+233241234567'
 * formatPhoneNumber('+1234567890', '+1') // Returns '+1234567890'
 */
export const formatPhoneNumber = (phoneNumber, countryCode = '+233') => {
  if (!phoneNumber || typeof phoneNumber !== 'string') { return phoneNumber || ''; }

  let cleaned = phoneNumber.replace(/[\s\-()]/g, '');

  const normalizedCountryCode = countryCode.replace(/^\+/, '');

  if (cleaned.startsWith(normalizedCountryCode)) {
    return `+${cleaned}`;
  }

  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }

  return `+${normalizedCountryCode}${cleaned}`;
};

/**
 * Handles phone number input changes, removing leading zero for Ghana numbers
 * 
 * Automatically removes leading zero from Ghana phone numbers during input.
 * For other countries, passes the event through unchanged.
 * 
 * @param {Event} e - Input change event
 * @param {string|null} countryCode - Country code to determine if number is Ghana
 * @param {Function|null} onChange - Optional callback to handle the change
 * @returns {void}
 * 
 * @example
 * // In a React component
 * <input onChange={(e) => handlePhoneNumberChange(e, '+233', setPhoneNumber)} />
 */
export const handlePhoneNumberChange = (e, countryCode = null, onChange = null) => {
  let inputValue = e.target.value;
  if (inputValue && inputValue.startsWith('0')) {
    const isLikelyGhanaNumber = !countryCode || countryCode.includes('233');

    if (isLikelyGhanaNumber) {
      inputValue = inputValue.substring(1);

      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: inputValue
        }
      };

      if (onChange) {
        onChange(syntheticEvent);
      }
    }
  } else if (onChange) {
    onChange(e);
  }

  return inputValue;
};

/**
 * Formats phone number for API submission
 * 
 * Cleans and formats phone number for backend submission, removing all
 * non-numeric characters except + and ensuring country code is included.
 * 
 * @param {string} phoneNumber - The phone number to format
 * @param {string} countryCode - Country code with or without + prefix (default: '+233')
 * @returns {string} Formatted phone number ready for API submission
 * 
 * @example
 * formatPhoneForSubmit('024-123-4567') // Returns '+233241234567'
 * formatPhoneForSubmit('(024) 123-4567') // Returns '+233241234567'
 */
export const formatPhoneForSubmit = (phoneNumber, countryCode = '+233') => {
  if (!phoneNumber) { return phoneNumber; }

  let cleaned = phoneNumber.replace(/[^\d+]/g, '');

  const normalizedCountryCode = countryCode.replace(/^\+/, '');

  if (cleaned.startsWith(normalizedCountryCode)) {
    return `+${cleaned}`;
  }

  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }

  return `+${normalizedCountryCode}${cleaned}`;
};

/**
 * Cleans phone number for backend API (removes + and leading zero)
 * 
 * Removes country code prefix (+) and leading zero from phone numbers
 * to match backend API expectations. Returns null/undefined/empty as-is.
 * 
 * @param {string|number|null|undefined} value - Phone number to clean
 * @returns {string|null|undefined} Cleaned phone number or original value if empty
 * 
 * @example
 * cleanNumberForBackend('+233241234567') // Returns '233241234567'
 * cleanNumberForBackend('0241234567') // Returns '241234567'
 * cleanNumberForBackend(null) // Returns null
 */
export const cleanNumberForBackend = (value) => {
  if (value === null || value === undefined || value === '') { return value; }

  let cleaned = String(value).trim();

  cleaned = cleaned.replace(/^\+/, '');

  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }

  return cleaned;
};
